import { c as _c } from "react/compiler-runtime";
import { Link } from '@/components/links';
import { mergeClasses } from '@/utils/style';
import Image from 'next/image';
import React from 'react';
import { EshopBox as EshopBoxType, SvgComponent } from './types';
import { logEvent } from '@/analytics/events';
import StoreIcon from '@/assets/icons/heroIcons/Store.svg';
import ArrowTopRightIcon from '@/assets/icons/heroIcons/Arrow-top-right.svg';
import LogoSkipPay from '@/assets/logos/skippay-circles.svg';
import { Text } from '@/components/typography';
import { FilledButton } from '@/components/buttons';
import { useTranslation } from 'react-i18next';
function hasEshopLogo(logoUrl: string | SvgComponent) {
  if (typeof logoUrl !== 'string') return true;
  return logoUrl && !/\/static\/img\/skippay/.test(logoUrl);
}
function isSvgUrl(url: unknown): url is string {
  return typeof url === 'string' && url.split('?')[0].endsWith('.svg');
}
function LogoImage(t0) {
  const $ = _c(23);
  const {
    image,
    loading,
    priority,
    imageSizes
  } = t0;
  const {
    t
  } = useTranslation("core");
  let t1;
  if ($[0] !== image.title || $[1] !== t) {
    t1 = t("partnersLogoAlt", {
      name: image.title
    });
    $[0] = image.title;
    $[1] = t;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const alt = t1;
  const t2 = image.size === "cover" ? "object-cover" : "object-contain";
  let t3;
  if ($[3] !== t2) {
    t3 = mergeClasses("h-full w-full object-center", t2);
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const className = t3;
  if (typeof image.url === "function") {
    const SvgLogo = image.url;
    let t4;
    if ($[5] !== SvgLogo || $[6] !== alt || $[7] !== className) {
      t4 = <SvgLogo aria-label={alt} className={className} />;
      $[5] = SvgLogo;
      $[6] = alt;
      $[7] = className;
      $[8] = t4;
    } else {
      t4 = $[8];
    }
    return t4;
  }
  if (isSvgUrl(image.url)) {
    let t4;
    if ($[9] !== alt || $[10] !== className || $[11] !== image.url) {
      t4 = <img src={image.url} alt={alt} className={className} />;
      $[9] = alt;
      $[10] = className;
      $[11] = image.url;
      $[12] = t4;
    } else {
      t4 = $[12];
    }
    return t4;
  }
  const t4 = priority ? "high" : "auto";
  let t5;
  if ($[13] !== alt || $[14] !== className || $[15] !== image.height || $[16] !== image.url || $[17] !== image.width || $[18] !== imageSizes || $[19] !== loading || $[20] !== priority || $[21] !== t4) {
    t5 = <Image src={image.url} alt={alt} width={image.width} height={image.height} sizes={imageSizes} className={className} loading={loading} priority={priority} fetchPriority={t4} />;
    $[13] = alt;
    $[14] = className;
    $[15] = image.height;
    $[16] = image.url;
    $[17] = image.width;
    $[18] = imageSizes;
    $[19] = loading;
    $[20] = priority;
    $[21] = t4;
    $[22] = t5;
  } else {
    t5 = $[22];
  }
  return t5;
}
const getComponentClassName = (size: 'sm' | 'md' | undefined) => {
  return mergeClasses('relative block basis-2/4-gap-4 overflow-hidden rounded-2xl border-blue-900 bg-white shadow-outer-sm sm:basis-1/3-gap-4 md:basis-1/4-gap-4', {
    'xl:basis-1/6-gap-4': size === 'sm'
  }, {
    'lg:basis-1/4-gap-8': size === 'md'
  });
};
export function EshopBoxPlaceholder(t0) {
  const $ = _c(10);
  const {
    size
  } = t0;
  if (size === "wide") {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <div className="block h-[5.125rem] animate-pulse rounded-2xl border border-gray-100 bg-gray-200" />;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  let t1;
  if ($[1] !== size) {
    t1 = getComponentClassName(size);
    $[1] = size;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== size) {
    t2 = mergeClasses("block aspect-[16/10] animate-pulse bg-gray-200", {
      "md:aspect-[14/10]": size === "sm"
    }, {
      "md:aspect-[3/2]": size === "md"
    });
    $[3] = size;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t2) {
    t3 = <span className={t2} />;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t1 || $[8] !== t3) {
    t4 = <div className={t1}>{t3}</div>;
    $[7] = t1;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}
export function EshopBox(props) {
  const $ = _c(46);
  const {
    badgeType,
    image,
    link,
    size,
    section,
    index,
    loading,
    priority,
    imageSizes
  } = props;
  let t0;
  if ($[0] !== image.url) {
    t0 = hasEshopLogo(image.url);
    $[0] = image.url;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const hasLogo = t0;
  let t1;
  if ($[2] !== image.title || $[3] !== index || $[4] !== link || $[5] !== section) {
    t1 = function logOnSelect() {
      logEvent("select_promotion", {
        ecommerce: {
          items: [{
            creative_name: image.title,
            creative_slot: `${section}_${index + 1}`,
            location_id: link,
            promotion_id: "affil_promo",
            promotion_name: image.title
          }]
        }
      });
    };
    $[2] = image.title;
    $[3] = index;
    $[4] = link;
    $[5] = section;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const logOnSelect = t1;
  if (size === "wide") {
    let t2;
    if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <ArrowTopRightIcon className="h-6 w-6" />;
      $[7] = t2;
    } else {
      t2 = $[7];
    }
    let t3;
    if ($[8] !== badgeType) {
      t3 = badgeType === "SKIP_PAY" && <span className="absolute bottom-full left-full flex h-6 w-6 -translate-x-2/3 translate-y-2/3 items-center justify-center rounded-full border-[2px] border-white bg-blue-500 text-white shadow-sm"><LogoSkipPay className="h-[70%] w-[70%]" /></span>;
      $[8] = badgeType;
      $[9] = t3;
    } else {
      t3 = $[9];
    }
    let t4;
    if ($[10] !== hasLogo || $[11] !== image || $[12] !== loading || $[13] !== priority) {
      t4 = hasLogo ? <LogoImage image={image} loading={loading} priority={priority} /> : <StoreIcon className="h-10 w-10 text-blue-500 transition group-hover:text-white" />;
      $[10] = hasLogo;
      $[11] = image;
      $[12] = loading;
      $[13] = priority;
      $[14] = t4;
    } else {
      t4 = $[14];
    }
    let t5;
    if ($[15] !== t3 || $[16] !== t4) {
      t5 = <span className="border-light-gray-100 relative flex h-20 w-24 items-center justify-center border-r border-solid p-3 transition group-hover:border-blue-400">{t3}{t4}</span>;
      $[15] = t3;
      $[16] = t4;
      $[17] = t5;
    } else {
      t5 = $[17];
    }
    let t6;
    if ($[18] !== image.title || $[19] !== link || $[20] !== logOnSelect || $[21] !== t5) {
      t6 = <FilledButton analyticsParam="eshopBox" testId="eshopBox" to={link} variant="xs" onClick={logOnSelect} target="_blank" className="group w-full! gap-4 rounded-2xl border border-gray-100 p-0! pr-3! break-words shadow-transparent hover:border-blue-500" arrowIcon={t2} icon={t5}>{image.title}</FilledButton>;
      $[18] = image.title;
      $[19] = link;
      $[20] = logOnSelect;
      $[21] = t5;
      $[22] = t6;
    } else {
      t6 = $[22];
    }
    return t6;
  }
  let t2;
  if ($[23] !== size) {
    t2 = getComponentClassName(size);
    $[23] = size;
    $[24] = t2;
  } else {
    t2 = $[24];
  }
  let t3;
  if ($[25] !== size) {
    t3 = mergeClasses("block aspect-[16/10]", {
      "md:aspect-[14/10]": size === "sm"
    }, {
      "md:aspect-[3/2]": size === "md"
    });
    $[25] = size;
    $[26] = t3;
  } else {
    t3 = $[26];
  }
  const t4 = image.size === "contain";
  let t5;
  if ($[27] !== t4) {
    t5 = mergeClasses("block h-full", {
      "p-5": t4
    });
    $[27] = t4;
    $[28] = t5;
  } else {
    t5 = $[28];
  }
  let t6;
  if ($[29] !== hasLogo || $[30] !== image || $[31] !== imageSizes || $[32] !== loading || $[33] !== priority) {
    t6 = hasLogo ? <LogoImage image={image} loading={loading} priority={priority} imageSizes={imageSizes} /> : <span className="flex h-full flex-col items-center justify-center gap-1 text-center"><StoreIcon className="h-10 w-10 text-blue-200" /><Text className="line-clamp-3 text-xs font-semibold md:text-sm lg:line-clamp-2">{image.title}</Text></span>;
    $[29] = hasLogo;
    $[30] = image;
    $[31] = imageSizes;
    $[32] = loading;
    $[33] = priority;
    $[34] = t6;
  } else {
    t6 = $[34];
  }
  let t7;
  if ($[35] !== t5 || $[36] !== t6) {
    t7 = <span className={t5}>{t6}</span>;
    $[35] = t5;
    $[36] = t6;
    $[37] = t7;
  } else {
    t7 = $[37];
  }
  let t8;
  if ($[38] !== t3 || $[39] !== t7) {
    t8 = <span className={t3}>{t7}</span>;
    $[38] = t3;
    $[39] = t7;
    $[40] = t8;
  } else {
    t8 = $[40];
  }
  let t9;
  if ($[41] !== link || $[42] !== logOnSelect || $[43] !== t2 || $[44] !== t8) {
    t9 = <Link analyticsParam="eshopBox" testId="eshopBox" to={link} target="_blank" className={t2} onClick={logOnSelect}>{t8}</Link>;
    $[41] = link;
    $[42] = logOnSelect;
    $[43] = t2;
    $[44] = t8;
    $[45] = t9;
  } else {
    t9 = $[45];
  }
  return t9;
}